/**
 * Best For Index Page
 *
 * Lists all available activity guides with links to detailed best-for pages.
 * Public page for SEO purposes.
 * Route: /best-for
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchActivities, getRouteImageUrl, type Activity } from '../lib/api';

/**
 * Get the image URL for an activity
 */
function getActivityImageUrl(activity: Activity): string | null {
  return getRouteImageUrl(activity.cf_image_id, activity.image_url, 'card');
}

function ActivityCard({ activity }: { activity: Activity }) {
  const imageUrl = getActivityImageUrl(activity);
  const [imageError, setImageError] = useState(false);

  return (
    <Link
      to={`/best-for/${activity.id}`}
      className="block bg-neutral-900 border border-neutral-800 rounded-lg overflow-hidden hover:border-orange-500/50 transition-all group"
    >
      {/* Activity Image */}
      {imageUrl && !imageError ? (
        <div className="relative h-40 overflow-hidden">
          <img
            src={imageUrl}
            alt={activity.image_alt || `${activity.name} activity`}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
            onError={() => setImageError(true)}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/80 to-transparent" />
        </div>
      ) : (
        <div className="h-40 bg-neutral-800 flex items-center justify-center">
          <svg
            className="w-12 h-12 text-neutral-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
      )}

      {/* Content */}
      <div className="p-5">
        <h3 className="font-mono text-lg font-semibold text-white group-hover:text-orange-500 transition-colors">
          {activity.name}
        </h3>
        {activity.description && (
          <p className="text-neutral-400 text-sm mt-1 line-clamp-2">
            {activity.description}
          </p>
        )}
        <div className="mt-3 flex items-center gap-2 text-sm text-orange-500">
          <span>View gear guide</span>
          <svg
            className="w-4 h-4 transform group-hover:translate-x-1 transition-transform"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </Link>
  );
}

function BestForStructuredData({ activities }: { activities: Activity[] }) {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Best Gear Guides by Activity',
    description: 'Expert-curated gear recommendations for hiking, camping, trail running, and more.',
    numberOfItems: activities.length,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: activities.length,
      itemListElement: activities.map((activity, index) => {
        const imageUrl = getActivityImageUrl(activity);
        return {
          '@type': 'ListItem',
          position: index + 1,
          url: `https://notfuckingabout.com/best-for/${activity.id}`,
          name: `Best Gear for ${activity.name}`,
          ...(imageUrl && { image: imageUrl }),
        };
      }),
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
}

export function BestForIndex() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['activities'],
    queryFn: fetchActivities,
  });

  // Update document title
  useEffect(() => {
    document.title = 'Best Gear Guides by Activity | NFA';
    return () => {
      document.title = 'NFA - Gear Recommendations';
    };
  }, []);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6">
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6">
        <nav className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Home
          </Link>
        </nav>

        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-8 text-center">
          <h2 className="font-mono text-xl font-bold text-red-400 mb-2">Something went wrong</h2>
          <p className="text-neutral-400">
            We couldn't load the activity guides. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  const activities = data?.activities ?? [];

  return (
    <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6">
      <BestForStructuredData activities={activities} />

      {/* Breadcrumb */}
      <nav className="mb-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Home
        </Link>
      </nav>

      {/* Header */}
      <div className="mb-8">
        <h1 className="font-mono text-3xl sm:text-4xl font-bold text-white mb-4">
          Best Gear Guides
        </h1>
        <p className="text-neutral-400">
          Expert-curated gear recommendations for every adventure. Each guide features our top picks
          across budget, mid-range, and premium tiers.
        </p>
      </div>

      {/* Activities Grid */}
      {activities.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {activities.map((activity) => (
            <ActivityCard key={activity.id} activity={activity} />
          ))}
        </div>
      ) : (
        <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-8 text-center">
          <p className="text-neutral-400">No activity guides available yet.</p>
        </div>
      )}

      {/* SEO Content */}
      <div className="mt-16 pt-8 border-t border-neutral-800 prose prose-invert prose-sm max-w-none">
        <h2 className="font-mono text-xl font-bold text-white mb-4">Find the Right Gear</h2>
        <p className="text-neutral-400">
          Whether you're planning a multi-day hike, setting up camp in the wilderness,
          or pushing your limits on technical terrain, we've done the research so you don't have to.
        </p>
        <p className="text-neutral-400 mt-4">
          Each guide is built from real experience and focuses on gear that actually works,
          not just what's popular or heavily marketed. We recommend products across three tiers:
          budget options for those starting out, mid-range picks that offer the best value,
          and premium choices for when performance matters most.
        </p>
      </div>

      {/* CTA */}
      <div className="mt-12 bg-gradient-to-r from-orange-500/10 to-transparent border border-orange-500/20 rounded-lg p-6 sm:p-8">
        <h2 className="font-mono text-xl sm:text-2xl font-bold text-white mb-3">
          Need a custom kit?
        </h2>
        <p className="text-neutral-400 mb-6">
          Tell us about your specific trip and we'll build a complete gear list tailored to your adventure.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-black font-bold rounded-lg hover:bg-orange-400 transition-colors"
        >
          <span>Build your kit</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </Link>
      </div>
    </div>
  );
}
